
import { defineComponent, computed, onMounted, onUnmounted, ref } from "vue";
import CommonRecipeList from "@/components/commonRecipe/CommonRecipeList.vue";
import store from "@/store";
import router from "@/router";
import NativeClient, { TitleInfo } from "@/client/native-client";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import {
  FavoriteRecipeClips,
  FavoriteAddFolderRecipe,
  HTMLDivElementEvent
} from "@/types/favorites";
import {
  isEndOfScrollBottom,
  createFavoriteRecipesToCommonRecipes
} from "@/utils/favorites-util";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { isValidApiAccessToken } from "@/utils/common-utils";
import CommonUtil from "@/utils/common-utils";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";

export default defineComponent({
  name: "MyListSelectPage",
  components: { CommonRecipeList, CommonDesignDialog },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // WebAPIのリクエスト状況
    class MyListSelectrPageRequestType {
      type: number; // 0:なし、1:リスト、2:フォルダへの追加、3:追加読み込み
      constructor(type: number) {
        this.type = type;
      }
    }
    const refRequestType = ref(new MyListSelectrPageRequestType(0));

    // 追加有効
    const refAppendEnable = ref(false);

    // ログIDの設定
    setLogId(PAGE_ID.FAVORITES_MYLIST_SELECT);

    // 選択付きのレシピ一覧
    const selectMode = 2;

    // フォルダIDを取得
    const currentId = Number(router.currentRoute.value.query.folderId);

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "追加するレシピを選択"));
    CommonUtil.scroll({ top: 0, left: 0 });

    // テキスト
    const recentTitle = "最近追加したものから";
    const appendButtonText = "追加";

    // 選択したレシピのレシピID（）
    const checkedRecipes: FavoriteAddFolderRecipe[] = [];

    // API実行

    // マイリスト取得
    let totalCount = 0;
    let recipeCount = 0;
    const getMyList = async () => {
      NativeClient.sendDebugLog("MyListSelectPage getMyList");
      // 追加読み込みでないならばインジケータを表示
      if (refRequestType.value.type != 3) {
        refRequestType.value = new MyListSelectrPageRequestType(1);
        store.dispatch("common/startLoading");
      }
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        await store.dispatch("favorites/getMyList");
        refRequestType.value = new MyListSelectrPageRequestType(0);
        const result: FavoriteRecipeClips = store.getters["favorites/getMyList"];
        NativeClient.sendDebugLog("MyListSelectPage getMyList totalCount = " + result.totalCount);
        NativeClient.sendDebugLog("MyListSelectPage getMyList recipe count = " + result.clips.length);
        totalCount = result.totalCount;
        recipeCount = result.clips.length;
      }
      store.dispatch("common/stopLoading");
    };

    // マイリスト初期化して取得
    const initializeAndGetMyList = async () => {
      NativeClient.sendDebugLog("MyListSelectPage initializeAndGetMyList");
      totalCount = 0;
      recipeCount = 0;
      refRequestType.value = new MyListSelectrPageRequestType(0);

      // マイリスト初期化
      await store.dispatch("favorites/updateMyList", null);

      getMyList();
    };

    // 追加ボタン
    const selectAppend = async () => {
      if (refRequestType.value.type != 0) {
        return;
      }
      if (checkedRecipes.length == 0) {
        return;
      }
      NativeClient.sendDebugLog("MyListSelectPage Append");
      for (const recipeId of checkedRecipes) {
        NativeClient.sendDebugLog("MyListSelectPage Append recipeId = " + recipeId);
      }
      store.dispatch("common/startLoading");
      if (isValidApiAccessToken()) {
        refRequestType.value = new MyListSelectrPageRequestType(2);
        await NativeClient.getApiParams();
        await store.dispatch("favorites/addFolderRecipe", { folderId: currentId, recipes: checkedRecipes });
        refRequestType.value = new MyListSelectrPageRequestType(0);
        store.dispatch("common/stopLoading");
        // 前の画面に戻る
        router.go(-1);
      } else {
        store.dispatch("common/stopLoading");
      }
    };

    const execute = async () => {
      getMyList();
    };

    // Nativeからの更新要求
    const updatePage = (info: UpdatePageInfo) => {
      NativeClient.sendDebugLog("MyListSelectPage updatePage()");
      if (info.myList) {
        NativeClient.sendDebugLog("MyListSelectPage updatePage() update mylist");
        initializeAndGetMyList();
      }
    };

    // 表示状態の制御
    let isDisplayed = false;
    onMounted(() => {
      isDisplayed = true;

      // マイリスト更新
      NativeClient.registUpdatePageListener(updatePage);

      execute();

      // Beacon Log
      const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST_SELECT, null, []);
      NativeClient.sendViewedLog(logInfo);
    });
    onUnmounted(async () => {
      isDisplayed = false;
      // 画面から離れた場合、stateに保持したお気に入り一覧をクリア
      await store.dispatch("favorites/updateMyList", null);
    });

    // スクロール検知
    const onScroll = (event: HTMLDivElementEvent) => {
      NativeClient.sendDebugLog("MyListSelectPage onScroll");
      if (
        isEndOfScrollBottom(event) &&
        (refRequestType.value.type == 0) &&
        (recipeCount < totalCount)
      ) {
        NativeClient.sendDebugLog("MyListSelectPage onScroll detect");
        refRequestType.value = new MyListSelectrPageRequestType(3);
        getMyList();
      }
    };

    // APIリクエスト時のエラーハンドル
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const typeApiError = ref(9);
    /** ダイアログの制御 */
    const { closeDialog } = useApiErrorDialog();
    // const showApiErrorDialog = computed(() => {
    //   const apiError: ApiError = store.getters["common/getApiError"];
    //   NativeClient.sendDebugLog("MyListFolderPage showApiError");
    //   if (apiError == null) {
    //     NativeClient.sendDebugLog("MyListFolderPage showApiError no error");
    //     return false;
    //   } else if (!isDisplayed) {
    //     NativeClient.sendDebugLog("MyListFolderPage showApiError isDisplayed is false");
    //     return false;
    //   }
    //   return true;
    // });
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    const cancelHandler = () => {
      closeDialog();
      refRequestType.value = new MyListSelectrPageRequestType(0);
    }
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryHandler = () => {
      closeDialog();
      // switch (refRequestType.value.type) {
      //   case 1:
      //     refRequestType.value = new MyListSelectrPageRequestType(0);
      //     getMyList();
      //     break;
      //   case 2:
      //     refRequestType.value = new MyListSelectrPageRequestType(0);
      //     selectAppend();
      //     break;
      //   case 3:
      //     refRequestType.value = new MyListSelectrPageRequestType(0);
      //     getMyList();
      //     break;
      //   default:
      //     break;
      // }
    };

    // レシピ一覧の部品のインターフェースに合わせて、配列を生成する
    const recipeList = computed(() => {
      const result = store.getters["favorites/getMyList"];
      if (result == null || result.clips == null) {
        NativeClient.sendDebugLog("MyListSelectPage result null");
        return null;
      }
      const recipeList = createFavoriteRecipesToCommonRecipes(result.clips);
      return recipeList;
    });

    // レシピ選択
    const selectRecipe = (recipeId: number, type: string) => {
      // 選択されても無視する
      NativeClient.sendDebugLog("MyListSelectPage recipe");
      //NativeClient.transitionReceipDetail(recipeId, type);
    };
    // レシピのチェック
    const checkRecipe = (recipeId: number, groupId: number, check: boolean) => {
      NativeClient.sendDebugLog("MyListSelectPage testRrecipeList checkRecipe recipeId =" + recipeId + ", groupId = " + groupId + ", check = " + check);
      const target: FavoriteAddFolderRecipe = new FavoriteAddFolderRecipe(recipeId, groupId);
      const decisionFunc = (element: FavoriteAddFolderRecipe) => (element.recipeId == target.recipeId && element.groupId == target.groupId)
      if (check && !checkedRecipes.some(decisionFunc)) {
        checkedRecipes.push(target);
      } else if (!check && checkedRecipes.some(decisionFunc)) {
        const index = checkedRecipes.findIndex((element) => (element.recipeId == target.recipeId && element.groupId == target.groupId));
        checkedRecipes.splice(index, 1);
      }
      for (const recipe of checkedRecipes) {
        NativeClient.sendDebugLog("MyListSelectPage checkRecipe recipeId = " + recipe.recipeId + ", groupId = " + recipe.groupId);
      }
      if (checkedRecipes.length == 0) {
        refAppendEnable.value = false;
      } else {
        refAppendEnable.value = true;
      }
    };

    // スタイル
    const styleSelectAppendButton = computed(() => {
      NativeClient.sendDebugLog("MyListSelectPage styleSelectAppendButton");
      if (refAppendEnable.value == true) {
        NativeClient.sendDebugLog("MyListSelectPage styleSelectAppendButton refAppendEnable.value is true");
        return 'commonButtonText';
      }
      return 'commonButtonDisableText';
    });

    return {
      onScroll,
      recentTitle,
      appendButtonText,
      recipeList,
      selectRecipe,
      checkRecipe,
      selectMode,
      selectAppend,
      styleSelectAppendButton,
      handleApiError,
      typeApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      retryButtonTitle,
      okButtonTitle,
      cancelHandler,
      retryHandler
    };
  }
});
